import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';

const RecommendedProducts = ({ userId, type = 'personalized', limit = 6, categoryId = null, productId = null }) => {
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [meta, setMeta] = useState(null);

  useEffect(() => {
    const fetchRecommendations = async () => {
      try {
        setLoading(true);
        const token = localStorage.getItem('token');
        const config = {
          headers: token ? { Authorization: `Bearer ${token}` } : {}
        };

        let url = '';
        switch (type) {
          case 'personalized':
            url = `/api/recommendations/personalized?limit=${limit}`;
            break;
          case 'related':
            if (!productId) return;
            url = `/api/recommendations/related/${productId}?limit=${limit}`;
            break;
          case 'popular':
            if (!categoryId) return;
            url = `/api/recommendations/category/${categoryId}/popular?limit=${limit}`;
            break;
          case 'trending':
            url = `/api/recommendations/trending?limit=${limit}`;
            break;
          default:
            url = `/api/recommendations/personalized?limit=${limit}`;
        }

        const response = await axios.get(url, config);
        if (response.data.success) {
          setProducts(response.data.data);
          setMeta(response.data.meta);
        }
      } catch (err) {
        setError('לא הצלחנו לטעון המלצות כרגע');
      } finally {
        setLoading(false);
      }
    };

    fetchRecommendations();
  }, [type, limit, categoryId, productId]);

  const getTitleByType = () => {
    switch (type) {
      case 'personalized':
        return 'מומלץ במיוחד בשבילך';
      case 'related':
        return 'לקוחות שקנו מוצר זה קנו גם';
      case 'popular':
        return 'פופולרי בקטגוריה';
      case 'trending':
        return 'טרנדים חמים';
      default:
        return 'מומלץ';
    }
  };

  if (loading) {
    return (
      <div className="py-8">
        <div className="animate-pulse">
          <div className="h-8 bg-gray-200 rounded w-48 mb-4"></div>
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4">
            {[...Array(6)].map((_, i) => (
              <div key={i} className="bg-gray-200 h-64 rounded-lg"></div>
            ))}
          </div>
        </div>
      </div>
    );
  }

  if (error || !products || products.length === 0) {
    return null;
  }

  return (
    <div className="py-8">
      <div className="flex items-center justify-between mb-6">
        <h2 className="text-2xl font-bold text-gray-900">{getTitleByType()}</h2>
        {meta && (
          <span className="text-sm text-gray-500">
            {meta.basedOn === 'collaborative_filtering' && `מבוסס על ${meta.ordersAnalyzed} הזמנות`}
            {meta.timeRange && `בזמן: ${meta.timeRange}`}
          </span>
        )}
      </div>

      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4">
        {products.map((product) => (
          <Link
            key={product.id}
            to={`/products/${product.id}`}
            className="group bg-white rounded-lg shadow-sm hover:shadow-md transition-shadow duration-200 overflow-hidden"
          >
            <div className="aspect-square overflow-hidden bg-gray-100">
              <img
                src={
                  product.images && product.images.length > 0
                    ? `${process.env.REACT_APP_API_URL || '/api'}${product.images[0]}`
                    : '/placeholder-product.png'
                }
                alt={product.name}
                className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-200"
                loading="lazy"
                onError={(e) => {
                  e.target.src = '/placeholder-product.png';
                }}
              />
            </div>
            
            <div className="p-3">
              <h3 className="text-sm font-semibold text-gray-900 mb-1 line-clamp-2 group-hover:text-blue-600">
                {product.name}
              </h3>
              
              {product.category && (
                <p className="text-xs text-gray-500 mb-2">
                  {product.category.name}
                </p>
              )}

              <div className="flex items-center justify-between">
                <span className="text-lg font-bold text-gray-900">
                  ₪{parseFloat(product.price_b2c).toFixed(2)}
                </span>
                
                {product.rating_average > 0 && (
                  <div className="flex items-center text-xs text-gray-600">
                    <span className="text-yellow-500 mr-1">★</span>
                    <span>{parseFloat(product.rating_average).toFixed(1)}</span>
                  </div>
                )}
              </div>

              {product.stock_quantity < 10 && product.stock_quantity > 0 && (
                <p className="text-xs text-orange-600 mt-1">
                  נותרו רק {product.stock_quantity} במלאי
                </p>
              )}

              {product.co_purchase_frequency && (
                <div className="mt-2 text-xs text-blue-600 bg-blue-50 px-2 py-1 rounded">
                  נקנה ביחד {product.co_purchase_frequency} פעמים
                </div>
              )}

              {product.recent_sales > 0 && (
                <div className="mt-2 text-xs text-green-600 bg-green-50 px-2 py-1 rounded">
                  🔥 {product.recent_sales} נמכרו לאחרונה
                </div>
              )}
            </div>
          </Link>
        ))}
      </div>
    </div>
  );
};

export default RecommendedProducts;
